import React, { FC } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams, To } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { Box, Container, Grid, Typography, Button, makeStyles } from '@dxb-web-ui-framework/design-system';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { GetRoutePathParams } from '../../Routes/routePaths';
import { useSelectedCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';

export interface GenericLayoutProps {
  backButton?: boolean;
  label?: string;
  dataTestId: string;
  background?: string;
  id?: string;
  isFullHeight?: boolean;
  backBtnUrl: (params: Partial<GetRoutePathParams>) => To;
  onBackOverride?: () => void;
}

type makeStylesProps = {
  isMobile: boolean;
  background?: string;
};

const useStyles = ({ isMobile, background }: makeStylesProps) =>
  makeStyles(({ breakpoints, spacing }) => ({
    root: {
      backgroundColor: background ? background : 'unset',
    },
    fullHeight: {
      height: '100%',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: spacing(17),
      paddingBottom: isMobile ? 0 : spacing(6),
      minHeight: '100vh',
    },
    backButton: {
      marginLeft: -spacing(1),
    },
    label: {
      marginBottom: spacing(5),
      [breakpoints.down('sm')]: {
        marginBottom: spacing(4),
        paddingLeft: spacing(3),
        paddingRight: spacing(3),
      },
    },
  }));

const GenericLayout: FC<GenericLayoutProps> = ({
  backButton,
  label,
  dataTestId,
  background,
  id,
  isFullHeight,
  backBtnUrl,
  onBackOverride,
  children,
}) => {
  const navigate = useNavigate();
  const customerId = useSelectedCustomerId();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const styles = useStyles({ isMobile, background })();
  const { accountId, cardId, transactionNo, activityLogId, fileId } = useParams();

  const onBackBtnClick = () => {
    if (onBackOverride) {
      onBackOverride();
    } else {
      navigate(backBtnUrl({ customerId, accountId, cardId, transactionNo, activityLogId, fileId }));
    }
  };

  return (
    <Grid component={'main'} container data-testid={dataTestId} className={styles.root}>
      <Container
        maxWidth='lg'
        disableGutters={isMobile}
        className={clsx(styles.container, { [styles.fullHeight]: isFullHeight && !isMobile })}>
        {backButton && !isMobile && (
          <Box pb={6} className={styles.backButton}>
            <Button
              id='generic-back-button' // it is used to hide it when step flow is completed
              aria-label='Back'
              color='secondary'
              isIconButton
              size='small'
              title='Back'
              variant='text'
              data-testid='navigate-back-button'
              onClick={onBackBtnClick}>
              <ArrowBackIcon />
            </Button>
          </Box>
        )}

        {label && (
          <Box className={styles.label} id={id}>
            <Typography component={'h2'} variant={isMobile ? 'h5' : 'h4'}>
              {label}
            </Typography>
          </Box>
        )}

        {children}
      </Container>
    </Grid>
  );
};

export { GenericLayout };
